import { gql } from '@apollo/client';
import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { z } from 'zod';
import { CopyIcon } from '~components/ui/icons';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { SsoValidationStatus } from '~graphql/__generated__/types';
import { OrganizationSsoIndexPageDocument } from '~pages/[organization]/settings/sso/__generated__/Index.graphql';
import {
  usePublishOidcMutation,
  useSaveOidcProviderMutation,
} from './__generated__/OidcRegistrationModal.graphql';

const redirectUrl = `https://${process.env.REACT_APP_AUTH0_DOMAIN}/login/callback`;

// Types for form data
export interface OidcFormData {
  connectionName: string;
  issuerUrl: string;
  clientId: string;
  clientSecret: string;
}

export interface OidcInitialData {
  connectionName?: string;
  issuerUrl?: string;
  clientId: string;
  clientSecret?: string;
  validationStatus?: SsoValidationStatus | null;
}

// Schema definition for OIDC
const OidcSchema = z.object({
  connectionName: z.string(),
  issuerUrl: z.string(),
  clientId: z.string(),
  clientSecret: z.string(),
});

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  ssoId: string;
  initialData?: OidcInitialData;
  isEditing?: boolean;
}

export function OidcRegistrationModal({
  isOpen,
  onClose,
  onSuccess,
  ssoId,
  initialData,
  isEditing = false,
}: Props) {
  const initialFocusRef = useRef<HTMLInputElement | null>(null);

  return (
    <Modal initialFocusRef={initialFocusRef} isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <OidcRegistrationModalContent
          ssoId={ssoId}
          onClose={onClose}
          onSuccess={onSuccess}
          initialFocusRef={initialFocusRef}
          initialData={initialData}
          isEditing={isEditing}
        />
      </ModalContent>
    </Modal>
  );
}

interface ContentProps {
  ssoId: string;
  initialFocusRef: MutableRefObject<HTMLInputElement | null>;
  onClose: () => void;
  onSuccess?: () => void;
  initialData?: OidcInitialData;
  isEditing?: boolean;
}

function OidcRegistrationModalContent({
  ssoId,
  initialFocusRef,
  onClose,
  onSuccess,
  initialData,
  isEditing = false,
}: ContentProps) {
  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // Determine if we should start in read-only mode
  // Read-only when editing an SSO with a validationStatus (pending/completed/failed)
  const hasValidationStatus = initialData?.validationStatus != null;
  const [isEditMode, setIsEditMode] = useState(!hasValidationStatus);
  const [issuerUrlError, setIssuerUrlError] = useState<string | null>(null);

  const [saveOidc] = useSaveOidcProviderMutation({
    refetchQueries: [{ query: OrganizationSsoIndexPageDocument }],
    awaitRefetchQueries: true,
  });
  const [publishOidc] = usePublishOidcMutation({
    refetchQueries: [{ query: OrganizationSsoIndexPageDocument }],
    awaitRefetchQueries: true,
  });

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    reset,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<OidcFormData>({
    defaultValues: {
      connectionName: initialData?.connectionName || '',
      issuerUrl: initialData?.issuerUrl || '',
      clientId: initialData?.clientId || '',
      clientSecret: initialData?.clientSecret || '',
    },
    mode: 'onChange',
    resolver: zodResolver(OidcSchema),
  });

  // Reset form when initialData changes
  useEffect(() => {
    if (initialData) {
      reset({
        connectionName: initialData.connectionName || '',
        issuerUrl: initialData.issuerUrl || '',
        clientId: initialData.clientId || '',
        clientSecret: initialData.clientSecret || '',
      });
    }
  }, [initialData, reset]);

  const normalizeIssuerUrl = useCallback((url: string) => {
    let normalized = url.trim();
    normalized = normalized.replace(/\/+$/, '');
    normalized = normalized.replace(/\/\.well-known(\/openid-configuration)?$/, '');
    return normalized;
  }, []);

  // Fetch OIDC discovery configuration
  const fetchOidcConfig = useCallback(
    async (issuerUrl: string) => {
      if (!issuerUrl) return null;
      const baseIssuer = normalizeIssuerUrl(issuerUrl);
      const discoveryUrl = `${baseIssuer}/.well-known/openid-configuration`;
      const response = await fetch(discoveryUrl);
      if (!response.ok) throw new Error('Failed to fetch OIDC configuration');
      const config = await response.json();
      return {
        discoveryUrl,
        authorizationEndpoint: config.authorization_endpoint,
        jwksUri: config.jwks_uri,
      };
    },
    [normalizeIssuerUrl],
  );

  // Watch form values
  const connectionName = watch('connectionName');
  const clientId = watch('clientId');
  const clientSecret = watch('clientSecret');

  // Derived state
  const isReadOnly = hasValidationStatus && !isEditMode;

  // Submit requires all fields to have values
  const issuerUrl = watch('issuerUrl');
  const canSubmit = useMemo(() => {
    if (!isValid) return false;
    if (isReadOnly) return false;
    const hasConnectionName = connectionName && connectionName.trim().length > 0;
    const hasClientId = clientId && clientId.trim().length > 0;
    const hasClientSecret = clientSecret && clientSecret.trim().length > 0;
    const hasIssuerUrl = issuerUrl && issuerUrl.trim().length > 0;
    return hasConnectionName && hasClientId && hasClientSecret && hasIssuerUrl;
  }, [isValid, isReadOnly, connectionName, clientId, clientSecret, issuerUrl]);

  // Handle Edit button click
  const handleEditClick = () => {
    setIsEditMode(true);
  };

  // Handle Save (draft - upsert only, no publish)
  const handleSave = useCallback(async () => {
    const data = getValues();
    setIsSaving(true);
    try {
      // Fetch OIDC config
      let config;
      try {
        config = await fetchOidcConfig(data.issuerUrl);
      } catch {
        setIssuerUrlError(t('ssoIssuerUrlInvalid'));
        setIsSaving(false);
        return;
      }

      const normalizedIssuer = normalizeIssuerUrl(data.issuerUrl);
      await saveOidc({
        variables: {
          input: {
            id: ssoId,
            displayName: data.connectionName,
            clientId: data.clientId,
            clientSecret: data.clientSecret,
            issuer: normalizedIssuer,
            discoveryUrl:
              config?.discoveryUrl || `${normalizedIssuer}/.well-known/openid-configuration`,
            authorizationEndpoint: config?.authorizationEndpoint || undefined,
            jwksUri: config?.jwksUri || undefined,
          },
        },
      });

      toast({
        title: t('ssoSavedAsDraft'),
        description: t('ssoSavedAsDraftDescription'),
        status: 'success',
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      toast({
        title: t('errorSavingSso'),
        description: (e as Error).message,
        status: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  }, [getValues, saveOidc, ssoId, toast, onSuccess, onClose, fetchOidcConfig, normalizeIssuerUrl]);

  // Handle Submit (upsert + publish)
  const handleFormSubmit = useCallback(
    async (data: OidcFormData) => {
      try {
        const normalizedIssuer = normalizeIssuerUrl(data.issuerUrl);

        // Fetch config
        let config;
        try {
          config = await fetchOidcConfig(data.issuerUrl);
        } catch {
          setIssuerUrlError(t('ssoIssuerUrlInvalid'));
          return;
        }

        // First save the data
        await saveOidc({
          variables: {
            input: {
              id: ssoId,
              displayName: data.connectionName,
              clientId: data.clientId,
              clientSecret: data.clientSecret,
              issuer: normalizedIssuer,
              discoveryUrl:
                config?.discoveryUrl || `${normalizedIssuer}/.well-known/openid-configuration`,
              authorizationEndpoint: config?.authorizationEndpoint || undefined,
              jwksUri: config?.jwksUri || undefined,
            },
          },
          refetchQueries: [],
          awaitRefetchQueries: false,
        });

        // Then publish
        await publishOidc({
          variables: { id: ssoId },
        });

        toast({
          title: t('ssoSubmitted'),
          description: t('ssoSubmittedDescription'),
          status: 'success',
        });
        onSuccess?.();
        onClose();
      } catch (e) {
        toast({
          title: t('errorSubmittingSso'),
          description: (e as Error).message,
          status: 'error',
        });
      }
    },
    [saveOidc, publishOidc, ssoId, toast, onSuccess, onClose, fetchOidcConfig, normalizeIssuerUrl],
  );

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalHeader>{isEditing ? t('editSsoRegistration') : t('ssoRegistration')}</ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        <Stack spacing={4}>
          <Text fontSize="sm" color="gray.600">
            {t('fillSsoRegistrationDetails')}
          </Text>

          {/* Connection Name */}
          <FormControl isRequired isInvalid={Boolean(errors.connectionName)}>
            <FormLabel fontWeight="semibold">{t('connectionName')}</FormLabel>
            <Input
              {...register('connectionName')}
              ref={(e) => {
                register('connectionName').ref(e);
                initialFocusRef.current = e;
              }}
              isReadOnly={isReadOnly}
              bg={isReadOnly ? 'gray.50' : undefined}
            />
            <FormErrorMessage>{errors.connectionName?.message}</FormErrorMessage>
          </FormControl>

          {/* Redirect URL (read-only) */}
          <FormControl>
            <FormLabel fontWeight="semibold">{t('redirectUrl')}</FormLabel>
            <InputGroup>
              <Input value={redirectUrl} isReadOnly bg="gray.50" />
              <InputRightElement>
                <IconButton
                  aria-label="Copy Redirect URL"
                  icon={<CopyIcon boxSize={4} />}
                  size="sm"
                  variant="ghost"
                  colorScheme="gray"
                  onClick={() => {
                    navigator.clipboard.writeText(redirectUrl);
                    toast({ title: t('copiedToClipboard'), status: 'info', duration: 1000 });
                  }}
                />
              </InputRightElement>
            </InputGroup>
          </FormControl>

          {/* Redirect URL warning: only in edit mode */}
          {!isReadOnly && (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">{t('ssoOidcRedirectUrlWarning')}</Text>
            </Alert>
          )}

          {/* Client ID */}
          <FormControl isRequired isInvalid={Boolean(errors.clientId)}>
            <FormLabel fontWeight="semibold">{t('clientId')}</FormLabel>
            <Input
              {...register('clientId')}
              isReadOnly={isReadOnly}
              bg={isReadOnly ? 'gray.50' : undefined}
            />
            <FormErrorMessage>{errors.clientId?.message}</FormErrorMessage>
          </FormControl>

          {/* Client Secret */}
          <FormControl isRequired isInvalid={Boolean(errors.clientSecret)}>
            <FormLabel fontWeight="semibold">{t('clientSecret')}</FormLabel>
            <Input
              {...register('clientSecret')}
              isDisabled={isReadOnly}
              isReadOnly={isReadOnly}
              bg={isReadOnly ? 'gray.50' : undefined}
              type={isReadOnly ? 'password' : 'text'}
              placeholder={
                isEditMode && hasValidationStatus ? t('enterNewClientSecret') : undefined
              }
            />
            <FormErrorMessage>{errors.clientSecret?.message}</FormErrorMessage>
          </FormControl>

          {/* Issuer URL */}
          <FormControl isRequired isInvalid={Boolean(errors.issuerUrl) || Boolean(issuerUrlError)}>
            <FormLabel fontWeight="semibold">{t('issuerUrl')}</FormLabel>
            <InputGroup>
              <Input
                {...register('issuerUrl', {
                  onChange: () => {
                    if (issuerUrlError) setIssuerUrlError(null);
                  },
                })}
                isReadOnly={isReadOnly}
                bg={isReadOnly ? 'gray.50' : undefined}
              />
            </InputGroup>
            <FormErrorMessage>{errors.issuerUrl?.message || issuerUrlError}</FormErrorMessage>
          </FormControl>

          {/* Info and warning banners: only shown in edit mode */}
          {!isReadOnly && (
            <>
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  <Trans
                    i18nKey="ssoOidcImplicitFlowInfo"
                    components={{ bold: <Text as="span" fontWeight="bold" /> }}
                  />
                </Text>
              </Alert>
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">{t('ssoOidcEditWarning')}</Text>
              </Alert>
            </>
          )}
        </Stack>
      </ModalBody>

      <ModalFooter>
        <Button
          variant="ghost"
          colorScheme="gray"
          onClick={onClose}
          isDisabled={isSubmitting || isSaving}
        >
          {t('cancel')}
        </Button>

        {isReadOnly ? (
          /* Read-only mode: Show Edit button */
          <Button
            type="button"
            variant="ghost"
            colorScheme="gray"
            marginLeft="3"
            onClick={handleEditClick}
          >
            {t('edit')}
          </Button>
        ) : (
          /* Edit mode: Show Save button */
          <Button
            type="button"
            variant="ghost"
            colorScheme="gray"
            marginLeft="3"
            onClick={handleSave}
            isDisabled={!isDirty || isSubmitting || isSaving || hasValidationStatus}
            isLoading={isSaving}
          >
            {t('save')}
          </Button>
        )}

        <Button
          variant="solid"
          colorScheme="blue"
          marginLeft="3"
          type="submit"
          isDisabled={!canSubmit || isSubmitting || isSaving}
          isLoading={isSubmitting}
        >
          {t('submit')}
        </Button>
      </ModalFooter>
    </form>
  );
}

// GraphQL mutations
OidcRegistrationModal.graphql = {
  mutations: {
    SaveOidcProvider: gql`
      mutation SaveOidcProvider($input: SsoProviderOidcUpsertInput!) {
        ssoProviderOidcUpsert(input: $input) {
          displayName
          validationStatus
          strategy
          options {
            clientId
            issuer
          }
        }
      }
    `,
    PublishOidc: gql`
      mutation PublishOidc($id: ID!) {
        ssoProviderOidcPublish(input: { id: $id }) {
          id
          domain
          verificationStatus
          provider {
            ... on OidcProvider {
              displayName
              validationStatus
              strategy
            }
          }
        }
      }
    `,
  },
};
